import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, User, ArrowRight, UserPlus } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const Login = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    username: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error("Please enter both username and password");
      return;
    }

    setLoading(true);

    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const response = await axios.post(`${API}${endpoint}`, {
        username: credentials.username,
        password: credentials.password
      });

      const data = response.data;
      
      if (data.success) {
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("username", data.username);
        localStorage.setItem("userId", data.userId);
        
        // Store onboarding status for the dashboard to use immediately
        if (data.onboardingComplete) {
          localStorage.setItem(`onboardingComplete_${data.userId}`, "true");
        }
        
        toast.success(isRegister ? "Account created!" : "Login successful!");
        
        // Navigate based on onboarding status
        if (data.onboardingComplete) {
          navigate(`/dashboard/${data.userId}`);
        } else {
          navigate("/dashboard/new");
        }
      } else {
        toast.error(data.message || "Authentication failed");
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.message || "Authentication failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center relative overflow-hidden">
      {/* Cyber Grid Background */}
      <div className="absolute inset-0 cyber-grid opacity-20" />

      {/* Login Card */}
      <motion.div 
        className="relative z-10 w-full max-w-md mx-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="glass-card holo-border p-8 relative overflow-hidden">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_pro-advisors-4/artifacts/00joaz6p_017767_Logo%20FInal%20File-01.png" 
                alt="Intelligent Sales Machines" 
                className="h-32 w-auto"
              />
            </div>
            <p className="text-gray-500 text-sm">Enterprise Sales Intelligence Platform</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <Input
                  type="text"
                  value={credentials.username}
                  onChange={(e) =>
                    setCredentials({ ...credentials, username: e.target.value })
                  }
                  className="w-full bg-white/5 border-white/10 text-white pl-10 rounded-xl focus:border-neon-cyan transition-all placeholder:text-gray-600"
                  placeholder="Enter your username"
                  disabled={loading}
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <Input
                  type="password"
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials({ ...credentials, password: e.target.value })
                  }
                  className="w-full bg-white/5 border-white/10 text-white pl-10 rounded-xl focus:border-neon-cyan transition-all placeholder:text-gray-600"
                  placeholder="Enter your password"
                  disabled={loading}
                  data-testid="login-password"
                />
              </div>
            </div>

            <div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold py-3 rounded-xl transition-all border-0 text-base"
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full mr-2 animate-spin" />
                    {isRegister ? "Creating Account..." : "Signing In..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isRegister ? (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Create Account
                      </>
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Toggle Register/Login */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-neon-cyan hover:text-neon-cyan/80 transition-colors"
              data-testid="toggle-register"
            >
              {isRegister ? "Already have an account? Sign In" : "Need an account? Register"}
            </button>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center mt-6 text-xs text-gray-600">
          © Copyright 2024 Intelligent Sales Machines, Inc. – All rights reserved.
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
